"use client";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import RiskPerformanceCard from "../others/RiskPerformanceCard";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { apiPost } from "@/utils/apiClient";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { useIsMobile } from "@/utils/useIsMobile";
import Icon from "@/components/icons/Icons"; 
import SectionHeading from "../others/SectionHeading";
import InformationButton from "@/components/ui/InformationButton";

interface NavItem {
  date: string;
  nav: number;
}

type RiskDataItem = {
  risk_id: number;
  title: string;
  sh_desc: string;
  from_time: string;
  till_time: string;
  logo: string;
  created_at: string;
  updated_at: string;
  risk_fund: {
    id: number;
    isin: string;
    risk_management_id: number;
    fall: number;
    fall_days: number;
    down_fall_days: number;
    start_date: string;
    end_date: string;
    created_at: string;
    updated_at: string;
  };
  benchmark_risk: {
    id: number | null;
    fall: number | null;
    fall_days: number | null;
    created_at: string | null;
    updated_at: string | null;
  };
};

type Props = {
  riskData: RiskDataItem[];
  schemeCode: string;
  kra: string;
  benchmarkId: number;
};

const MarketCrashResponseChart: React.FC<Props> = ({ riskData, schemeCode, kra, benchmarkId }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [schemeNavData, setSchemeNavData] = useState<[number, number][]>([]);
  const [benchNavData, setBenchNavData] = useState<[number, number][]>([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [fundStart, setFundStart] = useState<number | null>(null);

  const [minFund, setMinFund] = useState<{ minValue: number; daysToMin: number; recoveryDays: number | null } | null>(null);
  const [minBenchmark, setMinBenchmark] = useState<{ minValue: number; daysToMin: number; recoveryDays: number | null } | null>(null);

  const chartComponentRef = useRef<HighchartsReactRefObject>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isMobile = useIsMobile(631);
  const fetchId = useRef(0);

  const activeItem = riskData[activeIndex];
  const getYear = (dateStr: string) => new Date(dateStr).getFullYear();

  const parseData = (navData: NavItem[]): [number, number][] => {
    return navData
      .map((item) => {
        const timestamp = new Date(item.date).getTime();
        if (!isNaN(timestamp)) return [timestamp, item.nav];
        return null;
      })
      .filter((item): item is [number, number] => item !== null);
  };

  const convertToPercentageChange = (data: [number, number][]): [number, number][] => {
    if (!data.length) return [];
    const baseValue = data[0][1];
    return data.map(([timestamp, nav]) => {
      const percentChange = ((nav - baseValue) / baseValue) * 100;
      const clamped = Math.max(Math.min(percentChange, 100), -100);
      return [timestamp, clamped];
    });
  };

  const filterByDateRange = (data: [number, number][], from: string, till: string): [number, number][] => {
    const day = 24 * 60 * 60 * 1000;
    const fromTime = new Date(from).getTime() - 3 * day;
    const tillTime = new Date(till).getTime() + 3 * day;
    return data.filter(([timestamp]) => timestamp >= fromTime && timestamp <= tillTime);
  };

  const getFallAndRecoveryStats = (navData: [number, number][], from: string): { minValue: number; daysToMin: number; recoveryDays: number | null } | null => {
    if (!navData.length) return null;

    const baseValue = navData[0][1];
    let minValue = navData[0][1];
    let minTimestamp = navData[0][0];

    // Find minimum
    navData.forEach(([timestamp, nav]) => {
      if (nav < minValue) {
        minValue = nav;
        minTimestamp = timestamp;
      }
    });

    let recoveryTimestamp: number | null = null;
    for (const [timestamp, nav] of navData) {
      if (timestamp > minTimestamp && nav >= baseValue) {
        recoveryTimestamp = timestamp;
        break;
      }
    }

    const percentDrop = ((minValue - baseValue) / baseValue) * 100;
    const daysToMin = Math.max(0, Math.round((minTimestamp - new Date(from).getTime()) / (1000 * 60 * 60 * 24)));

    const recoveryDays = recoveryTimestamp ? Math.round((recoveryTimestamp - minTimestamp) / (1000 * 60 * 60 * 24)) : null;

    return { minValue: percentDrop, daysToMin, recoveryDays };
  };

  const fundLaunched = useMemo(() => {
    if (!fundStart || !activeItem?.from_time) return false;

    const crisisStart = new Date(activeItem.from_time).getTime();

    return fundStart < crisisStart;
  }, [activeItem, fundStart]);

  useEffect(() => {
    if (!riskData || riskData.length === 0) return;

    const validIndex = riskData.findIndex((item) => {
      if (!item?.risk_fund?.start_date || !item?.from_time) return false;
      return true;
    });

    setActiveIndex(validIndex !== -1 ? validIndex : 0);
  }, [riskData]);

  const fetchData = useCallback(
    async (useFallback = false) => {
      try {
        const id = ++fetchId.current;

        const payload = {
          scheme_code: schemeCode,
          kra,
          benchmark_id: useFallback ? 1 : benchmarkId || 1,
        };

        const data = await apiPost("scheme-details/nav-data", payload, { base: "base2" });
        if (id !== fetchId.current) return;

        const allSchemeData = parseData(data.result.scheme_nav);
        const allBenchmarkData = parseData(data.result.benchmark_nav || []);

        if (data.result.benchmark_nav.length === 0) {
          fetchData(true);
          return;
        }
        setFundStart(allSchemeData[0][0]);
        const filteredScheme = filterByDateRange(allSchemeData, activeItem.from_time, activeItem.till_time);
        const filteredBenchmark = filterByDateRange(allBenchmarkData, activeItem.from_time, activeItem.till_time);

        const schemePercentData = convertToPercentageChange(filteredScheme);
        const benchmarkPercentData = convertToPercentageChange(filteredBenchmark);

        setSchemeNavData(schemePercentData);
        setBenchNavData(benchmarkPercentData);

        const fundStats = getFallAndRecoveryStats(filteredScheme, activeItem.from_time);
        setMinFund(fundStats ?? null);

        const benchmarkStats = getFallAndRecoveryStats(filteredBenchmark, activeItem.from_time);
        setMinBenchmark(benchmarkStats ?? null);
      } catch (err) {
        console.error("Error fetching NAV data:", err);
      }
    },
    [schemeCode, kra, activeItem.from_time, activeItem.till_time, benchmarkId]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!riskData || riskData.length === 0 || !riskData[0].risk_id) return null;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "line",
      marginTop: 100,
    },
    title: {
      text: `${activeItem.title} - ${getYear(activeItem.from_time)}`,
      align: "left",
      x: 10,
      y: 35,
      style: {
        fontSize: "18px",
        fontFamily: "Inter, sans-serif",
        fontWeight: "700",
        color: "#1E1B39",
      },
    },
    xAxis: {
      type: "datetime",
      min: new Date(activeItem.from_time).getTime(),
      max: new Date(activeItem.till_time).getTime(),
      labels: {
        format: "{value:%b %Y}",
        style: {
          fontSize: "12px",
          fontWeight: "400",
          fontFamily: "Inter, sans-serif",
          color: "#615E83",
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineWidth: 1,
      labels: {
        format: "{value:.1f}%",
        style: {
          fontSize: "12px",
          fontWeight: "400",
          fontFamily: "Inter, sans-serif",
          color: "#615E83",
          whiteSpace: "nowrap",
        },
      },
    },
    legend: {
      layout: "horizontal",
      align: !isMobile ? "right" : "center",
      verticalAlign: !isMobile ? "top" : "bottom",
      floating: !isMobile ? true : false,
      x: !isMobile ? -10 : 0,
      y: !isMobile ? 15 : 0,
      itemStyle: {
        fontSize: !isMobile ? "14px" : "12px",
        fontFamily: "Inter, sans-serif",
        fontWeight: "400",
        color: "#615E83",
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: "%e %b %Y",
      valueSuffix: " %",
      valueDecimals: 2,
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false,
        },
      },
    },
    series: [
      {
        name: "Benchmark",
        data: benchNavData,
        color: "#6366F1",
        type: "line",
      },
      {
        name: "Fund",
        data: schemeNavData,
        color: "#06B6D4",
        type: "line",
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <section>
      <div className="flex items-center flex-wrap gap-3 mb-4">
        <SectionHeading heading="Market Crash Response Analysis" />

        <InformationButton keywords="market-crash-response" />

        <div className="relative shrink-0 w-full xs:max-w-fit ml-auto">
          <button ref={buttonRef} onClick={() => setDropdownOpen(!isDropdownOpen)} className="px-6 py-2.5 w-full text-sm flex items-center justify-between xxs:gap-10 rounded-full border border-[#1A51AA24] bg-[#EBF3FF] text-primaryBlue">
            {riskData[activeIndex]?.title} - {getYear(riskData[activeIndex]?.from_time)}
            <Icon.dropdownDownArrow className={`text-primaryBlue transition duration-350 ${isDropdownOpen ? "rotate-180" : ""}`} />
          </button>

          <CustomDropdown isOpen={isDropdownOpen} onClose={() => setDropdownOpen(false)} excludeRefs={[buttonRef]} className="mt-2 w-full z-1 shadow-md rounded-2xl overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {riskData.map((item, index) => (
                <li key={item.risk_id}>
                  <button
                    onClick={() => {
                      setActiveIndex(index);
                      setDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm truncate ${index === activeIndex ? "bg-[#EBF3FF] text-primaryBlue font-medium" : "text-[#202020A6] hover:text-primaryBlue hover:bg-gray-50"}`}
                    title={`${item.title} - ${getYear(item.from_time)}`}
                  >
                    {item.title} - {getYear(item.from_time)}
                  </button>
                </li>
              ))}
            </ul>
          </CustomDropdown>
        </div>
      </div>
      <div className="mb-6">
        <div className={`bg-white rounded-2xl h-400 shadow-sm px-4 pb-3 ${!fundLaunched && "flex items-center justify-center"}`}>
          {!fundLaunched ? (
            <div className="max-w-72">
              <Icon.errorWarningIcon className="text-primaryBlue w-20 h-20 mx-auto" />
              <p className="text-sm sm:text-base text-center text-[#444444] mt-4">Fund was not launched during this crisis period.</p>
            </div>
          ) : (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartComponentRef} />
          )}
        </div>
      </div>
      <RiskPerformanceCard fundLaunched={fundLaunched} fundFall={minFund?.minValue ?? null} benchmarkFall={minBenchmark?.minValue ?? null} fundDays={minFund?.recoveryDays ?? null} benchmarkDays={minBenchmark?.recoveryDays ?? null} />{" "}
    </section>
  );
};

export default MarketCrashResponseChart;
